/**
 * AI Piece Stats Cache
 *
 * In-memory cache for AI piece performance stats with batched Firebase writes.
 * - Fetches from Firebase once per AI model on first access
 * - Accumulates updates in memory during the session
 * - Batch-writes dirty models to Firebase periodically or on flush
 * - Falls back gracefully to empty stats on Firebase failure
 */

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { AIModel } from '../../zodiacEngine/AI/Core/AIVariants';
import { AIModelPieceStatsDoc, createEmptyStatsDoc } from './PieceStatsTypes';

const COLLECTION_NAME = 'ai_piece_stats';
const FLUSH_THRESHOLD = 3; // Flush after this many dirty updates

export class PieceStatsCache {
  private static instance: PieceStatsCache | null = null;

  private cache: Map<string, AIModelPieceStatsDoc> = new Map();
  private dirtyModels: Set<string> = new Set();
  private pendingFetches: Map<string, Promise<AIModelPieceStatsDoc | null>> = new Map();
  private dirtyCount = 0;

  static getInstance(): PieceStatsCache {
    if (!PieceStatsCache.instance) {
      PieceStatsCache.instance = new PieceStatsCache();
    }
    return PieceStatsCache.instance;
  }

  /**
   * Get stats for an AI model. Returns cached version if available,
   * otherwise fetches from Firebase (deduplicating concurrent requests).
   */
  async getStats(model: AIModel): Promise<AIModelPieceStatsDoc> {
    // Return cached version
    if (this.cache.has(model)) {
      return this.cache.get(model)!;
    }

    // Deduplicate concurrent fetches for the same model
    if (!this.pendingFetches.has(model)) {
      this.pendingFetches.set(model, this.fetchFromFirebase(model));
    }

    const result = await this.pendingFetches.get(model)!;
    this.pendingFetches.delete(model);

    const stats = result || createEmptyStatsDoc(model);
    // Migration safety: ensure new fields exist on old docs
    if (!stats.matchupStats) stats.matchupStats = {};
    this.cache.set(model, stats);
    return stats;
  }

  /**
   * Mark a model's stats as dirty (modified in memory, needs Firebase write).
   * Triggers auto-flush when threshold is reached.
   */
  markDirty(model: AIModel): void {
    this.dirtyModels.add(model);
    this.dirtyCount++;

    if (this.dirtyCount >= FLUSH_THRESHOLD) {
      this.flush();
    }
  }

  /**
   * Write all dirty models to Firebase.
   * Uses Promise.allSettled so one failure doesn't block others.
   */
  async flush(): Promise<void> {
    if (this.dirtyModels.size === 0) return;

    const modelsToFlush = [...this.dirtyModels];
    this.dirtyModels.clear();
    this.dirtyCount = 0;

    const promises = modelsToFlush.map(model => {
      const stats = this.cache.get(model);
      if (stats) {
        return this.writeToFirebase(model, stats);
      }
      return Promise.resolve();
    });

    await Promise.allSettled(promises);
  }

  /**
   * Check if stats exist in cache (without fetching).
   */
  hasCachedStats(model: AIModel): boolean {
    return this.cache.has(model);
  }

  private async fetchFromFirebase(model: string): Promise<AIModelPieceStatsDoc | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, model);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        return snap.data() as AIModelPieceStatsDoc;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  private async writeToFirebase(model: string, stats: AIModelPieceStatsDoc): Promise<void> {
    try {
      stats.updatedAt = Date.now();
      const docRef = doc(db, COLLECTION_NAME, model);
      await setDoc(docRef, stats);
    } catch (error) {
      // Non-fatal: data persists in cache, will retry on next flush
      // Re-mark as dirty so it retries
      this.dirtyModels.add(model);
    }
  }
}
